import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { RootState } from "store";
import useTitle from "hooks/useTitle";
import { useDebounce } from "use-debounce";
import useSearchQuery from "hooks/useSearchQuery";
import Practitioner from "api/types/models/practitioner";
import { setFilters, setPagination } from "slices/practitionerFilters";
import { PractitionerCategories } from "types/practitioners/categories";
import EditPractitionerModal from "components/modals/admin/practitioners/edit";
import {
  useGetPractitionersQuery,
  useGetSitesQuery,
  useUpdatePractitionerMutation,
} from "api";
import {
  Input,
  MapPinIcon,
  Pagination,
  PencilIcon,
  SearchIcon,
  Select,
  SelectOption,
  Table,
  Tag,
  Toggle,
} from "@mwi/ui";
import { useToast } from "hooks/useToast";

type Inputs = {
  category: PractitionerCategories;
  site: string | null;
  practitioners: Practitioner[];
};

const PractitionersPage = (): JSX.Element => {
  useTitle("Practitioners");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { practitionerId } = useParams();

  const { errorToast } = useToast();

  const { filtering, pagination } = useSelector((state: RootState) => ({
    filtering: state.practitionerFilters.filtering,
    pagination: state.practitionerFilters.pagination,
  }));

  const { control, watch, getValues, setValue } = useForm<Inputs>({
    defaultValues: {
      category: "species",
      practitioners: [],
    },
  });

  const [search, setSearch] = useState<string>();
  const [debouncedSearch] = useDebounce(search, 400);

  const [updatePractitioner] = useUpdatePractitionerMutation();

  const { data: sites } = useGetSitesQuery();

  const { data: practitioners, isFetching: fetchingPractitioners } =
    useGetPractitionersQuery({
      page: pagination.page,
      size: pagination.size,
      search: debouncedSearch,
      locationUuid:
        filtering.site && filtering.category === "reasons"
          ? filtering.site
          : undefined,
    });

  const searchParams = useMemo(
    () => ({
      category: filtering.category,
      site:
        filtering.site && filtering.category === "reasons"
          ? filtering.site
          : undefined,
      search: filtering.search,
      page: pagination.page,
    }),
    [filtering.category, filtering.site, filtering.search, pagination.page],
  );

  useSearchQuery(searchParams);

  useEffect(() => {
    if (practitioners) {
      setValue("practitioners", practitioners.data);
    }
  }, [practitioners]);

  useEffect(() => {
    // reset pagination to first page when changing practitioner category
    // or if search term has changed
    if (
      filtering.category !== getValues("category") ||
      ((filtering.search || debouncedSearch) &&
        filtering.search !== debouncedSearch)
    ) {
      const defaultSite =
        getValues("site") || (sites?.length ? sites[0]?.id : "");
      dispatch(setPagination({ page: 1 }));
      dispatch(
        setFilters({
          category: getValues("category"),
          site: getValues("category") === "reasons" ? defaultSite : null,
          search: debouncedSearch,
        }),
      );
      setValue("site", defaultSite);
    } else {
      dispatch(
        setFilters({
          category: getValues("category"),
          site: getValues("site"),
          search: debouncedSearch,
        }),
      );
    }
  }, [watch("site"), watch("category"), debouncedSearch]);

  useEffect(() => {
    if (sites && sites?.length && !getValues("site")) {
      setValue("site", sites[0]?.id);
    }
  }, [sites]);

  const handleTogglePractitioner = async (
    uuid: string,
    value: boolean,
    itemIdx: number,
  ) => {
    // Display toggled value as soon as possible
    setValue(`practitioners.${itemIdx}.is_enabled`, value);

    // Update practitioner, if it errors, revert back to original state
    await updatePractitioner({
      practitionerUuid: uuid,
      body: { contact_uuid: uuid, is_enabled: value },
    })
      .unwrap()
      .catch(() => {
        setValue(`practitioners.${itemIdx}.is_enabled`, !value);
        errorToast({
          title: "Unable to edit practitioner",
          message:
            "We're unable to update this practitioner at the moment. Please try again later.",
        });
      });
  };

  const categoryOptions = useMemo(
    () => [
      { id: "species", display: "Species" },
      { id: "reasons", display: "Reasons" },
    ],
    [],
  );

  return (
    <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
      <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
        <p className="font-semibold">
          Set details for practitioners&apos; expertise
        </p>
        <p>
          Manage the type of appointments in which certain practitioners do or
          do not specialise in
        </p>
      </div>

      <div className="col-span-full tablet:col-span-2">
        <Select
          name="category"
          size="small"
          control={control}
          placeholder="Select category"
        >
          {categoryOptions?.map((category) => (
            <SelectOption key={category.id} value={category.id}>
              {category.display}
            </SelectOption>
          ))}
        </Select>
      </div>

      {watch("category") === "reasons" && (
        <div className="tablet:col-start-4 tablet:col-end-6 desktop:col-start-6 desktop:col-end-8">
          <Select
            name="site"
            size="small"
            control={control}
            prefixIcon={<MapPinIcon />}
            placeholder="Select a vet site"
          >
            {sites?.map((s) => (
              <SelectOption key={s.id} value={s.id}>
                {s.name}
              </SelectOption>
            ))}
          </Select>
        </div>
      )}

      <div className="col-span-full tablet:col-start-6 desktop:col-start-8 desktop:col-end-11">
        <Input
          placeHolder="Search practitioners surname"
          prefixIcon={<SearchIcon />}
          size="small"
          onChange={(e) =>
            setSearch(e.target.value ? e.target.value : undefined)
          }
        />
      </div>

      <div className="my-8 overflow-x-auto col-span-full">
        <Table loading={fetchingPractitioners}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Name</Table.Item>
              <Table.Item as="th">Works with</Table.Item>
              <Table.Item as="th" className="w-[600px]">
                {filtering.category === "species"
                  ? "Selected species"
                  : "Selected reasons"}
              </Table.Item>
              <Table.Item as="th">Active</Table.Item>
              <Table.Item as="th">Edit</Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {watch("practitioners")?.length ? (
              watch("practitioners")?.map((practitioner, idx) => (
                <Table.Row key={practitioner.id}>
                  <Table.Item>
                    {practitioner.last_name && practitioner.first_name
                      ? `${practitioner.last_name}, ${practitioner.first_name}`
                      : `${practitioner.last_name ?? ""} ${practitioner.first_name ?? ""}`}
                  </Table.Item>
                  <Table.Item>
                    {filtering.category === "species" && (
                      <>
                        {practitioner.species_condition.species.length ===
                          0 && <Tag>N/A</Tag>}
                        {practitioner.species_condition.species?.length > 0 &&
                          practitioner.species_condition.works_with && (
                            <Tag variants="success">Yes</Tag>
                          )}
                        {practitioner.species_condition.species?.length > 0 &&
                          !practitioner.species_condition.works_with && (
                            <Tag variants="danger">No</Tag>
                          )}
                      </>
                    )}

                    {filtering.category === "reasons" && (
                      <>
                        {practitioner.reasons_condition.reasons.length ===
                          0 && <Tag>N/A</Tag>}
                        {practitioner.reasons_condition.reasons?.length > 0 &&
                          practitioner.reasons_condition.works_with && (
                            <Tag variants="success">Yes</Tag>
                          )}
                        {practitioner.reasons_condition.reasons?.length > 0 &&
                          !practitioner.reasons_condition.works_with && (
                            <Tag variants="danger">No</Tag>
                          )}
                      </>
                    )}
                  </Table.Item>
                  <Table.Item className="flex flex-wrap items-center h-full gap-2">
                    {filtering.category === "species" && (
                      <>
                        {practitioner.species_condition.species.length ===
                          0 && <Tag>N/A</Tag>}
                        {practitioner.species_condition.species
                          .slice(0, 5)
                          .map((species) => (
                            <Tag key={species.species_uuid}>
                              {species?.name ?? ""}
                            </Tag>
                          ))}
                        {practitioner.species_condition.species.length > 5 && (
                          <p className="text-sm font-semibold text-grey-dark">
                            +{" "}
                            {practitioner.species_condition.species.length - 5}{" "}
                            more
                          </p>
                        )}
                      </>
                    )}

                    {filtering.category === "reasons" && (
                      <>
                        {practitioner.reasons_condition.reasons.length ===
                          0 && <Tag>N/A</Tag>}
                        {practitioner.reasons_condition.reasons
                          .slice(0, 5)
                          .map((reason) => (
                            <Tag key={reason.reason_uuid}>
                              {reason?.name ?? ""}
                            </Tag>
                          ))}
                        {practitioner.reasons_condition.reasons.length > 5 && (
                          <p className="text-sm font-semibold text-grey-dark">
                            +{" "}
                            {practitioner.reasons_condition.reasons.length - 5}{" "}
                            more
                          </p>
                        )}
                      </>
                    )}
                  </Table.Item>
                  <Table.Item>
                    <Toggle
                      effectValue={practitioner.is_enabled}
                      onChange={(value) =>
                        handleTogglePractitioner(practitioner.id, value, idx)
                      }
                    />
                  </Table.Item>
                  <Table.Item>
                    <div
                      className="inline-block"
                      onClick={() =>
                        navigate(
                          `/admin/settings/practitioners/${practitioner.id}`,
                        )
                      }
                    >
                      <PencilIcon className="w-6 h-6 cursor-pointer text-primary" />
                    </div>
                  </Table.Item>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Item colSpan={5} className="text-center">
                  No practitioners found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <div className="col-span-full">
        <Pagination
          onPrevPage={() =>
            dispatch(setPagination({ page: pagination.page - 1 }))
          }
          prevPage={pagination.page > 1}
          currentPage={pagination.page}
          nextPage={practitioners?.meta?.has_next_page ?? false}
          onNextPage={() =>
            dispatch(setPagination({ page: pagination.page + 1 }))
          }
          loading={fetchingPractitioners}
        />
      </div>

      <EditPractitionerModal
        isOpen={!!practitionerId}
        onClose={() => navigate("/admin/settings/practitioners")}
      />
    </div>
  );
};

export default PractitionersPage;
